import { MapPin, Clock, Receipt, Star, Phone } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useSiteAssets } from "@/lib/siteAssets";

const MAP_SRC =
    "https://www.google.com/maps?q=" +
    encodeURIComponent("80/4, Bassettepet, A M Lane, B V K Iyengar Road cross, behind milk booth, Chickpet, Bengaluru 560053") +
    "&output=embed";

const PHONES = [
    { label: "+91 900 81 77 176", href: "tel:+919008177176" },
    { label: "+91 080 41689333", href: "tel:+918041689333" },
];

const CLIENT_PLACEHOLDERS = 6;

export default function LocationTrust() {
    const { profile } = useSiteAssets();
    const clients = (profile?.clients || []).filter(Boolean);

    return (
        <section id="visit" className="relative hud-bg py-20 lg:py-28 border-t border-line" data-testid="visit-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
                    <Reveal>
                        <Eyebrow className="mb-5">11 — Visit Us</Eyebrow>
                        <h2 className="font-display font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-ink">
                            Chickpet, <em className="text-copper">like always.</em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="md:max-w-sm">
                        <p className="text-mist leading-relaxed">
                            Walk in, call, or send your BOQ — the shop has been at the same Chickpet address
                            for decades.
                        </p>
                    </Reveal>
                </div>

                <div className="grid lg:grid-cols-2 gap-6 mb-14">
                    <Reveal>
                        <div className="range-card rounded-md border border-line overflow-hidden bg-white h-full min-h-[340px] transition-all duration-500">
                            <iframe
                                title="Roxy Cables on Google Maps — Chickpet, Bengaluru"
                                src={MAP_SRC}
                                className="w-full h-full min-h-[340px] border-0"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="visit-map"
                            />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="range-card rounded-md border border-line bg-white p-7 md:p-9 h-full flex flex-col gap-6 transition-all duration-500">
                            <div className="flex items-start gap-4">
                                <MapPin size={18} className="text-copper shrink-0 mt-1" />
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-1.5">Address</p>
                                    <p className="text-ink text-sm leading-relaxed" data-testid="visit-address">
                                        # 80/4, Bassettepet, A M Lane, B V K Iyengar Road cross,
                                        behind milk booth, Chickpet, Bengaluru 560053
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Phone size={18} className="text-copper shrink-0 mt-1" />
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-1.5">Call us</p>
                                    <div className="flex flex-col gap-1">
                                        {PHONES.map((p) => (
                                            <a
                                                key={p.href}
                                                href={p.href}
                                                data-testid={`visit-call-${p.href.slice(4)}`}
                                                className="text-ink text-sm font-medium hover:text-copper transition-colors duration-300"
                                            >
                                                {p.label}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Clock size={18} className="text-copper shrink-0 mt-1" />
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-1.5">Opening hours</p>
                                    <p className="text-ink text-sm" data-testid="visit-hours">
                                        {profile?.hours || "Hours to be added — check back soon"}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Receipt size={18} className="text-copper shrink-0 mt-1" />
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-1.5">GSTIN</p>
                                    <p className="text-ink text-sm font-mono" data-testid="visit-gstin">
                                        {profile?.gstin || "To be added"}
                                    </p>
                                </div>
                            </div>
                            {profile?.reviews_url && (
                                <a
                                    href={profile.reviews_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="visit-reviews-link"
                                    className="mt-auto inline-flex items-center justify-center gap-2 rounded-full border border-line px-6 py-3 font-mono uppercase tracking-[0.15em] text-[11px] text-ink hover:border-copper hover:text-copper transition-colors duration-300"
                                >
                                    <Star size={13} className="text-copper" /> Read our Google reviews
                                </a>
                            )}
                        </div>
                    </Reveal>
                </div>

                <Reveal>
                    <div data-testid="clients-strip">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim mb-5 text-center">
                            Clients &amp; projects we supply
                        </p>
                        <div className="flex flex-wrap justify-center gap-3">
                            {(clients.length ? clients : Array(CLIENT_PLACEHOLDERS).fill("")).map((c, i) => (
                                <span
                                    key={i}
                                    data-testid={`client-slot-${i}`}
                                    className={`font-mono text-[11px] uppercase tracking-[0.15em] px-5 py-3 rounded-md border ${
                                        c ? "border-line bg-white text-ink" : "border-dashed border-line text-dim/70"
                                    }`}
                                >
                                    {c || "Client name"}
                                </span>
                            ))}
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
